import { cs } from '../../../lib/currency';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Badge } from '../../ui/badge';
import EquipmentPicker from '../EquipmentPicker';
import api from '../../../services/api';
import PastDuePanel from './PastDuePanel';

const ROLE_COLOR = {
  admin: 'bg-slate-100 text-slate-700',
  staff: 'bg-indigo-50 text-indigo-700',
  coach: 'bg-amber-50 text-amber-700',
  parent: 'bg-blue-50 text-blue-700',
  player: 'bg-emerald-50 text-emerald-700',
  customer: 'bg-fuchsia-50 text-fuchsia-700',
};

/**
 * Admin "Walk-in Booking" form dialog.
 * Name field autocompletes from existing users (all roles). On pick, email and
 * phone auto-fill. If no match, backend creates a new customer and sends a
 * welcome email after the booking is persisted.
 */
export const AdminBookingModal = ({ open, onOpenChange, form, setForm, lanes, onSubmit }) => {
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [pickedId, setPickedId] = useState(null);
  const [loading, setLoading] = useState(false);
  // Saved Stripe PaymentMethods for the picked customer. Empty array
  // when the customer has none on file; null while we're still loading.
  const [savedPMs, setSavedPMs] = useState(null);
  const [loadingPMs, setLoadingPMs] = useState(false);
  // Live price breakdown — fetched from /lanes/admin/bookings/price-preview
  // whenever lane / duration / equipment / overrides / saved-PM change.
  // The CC surcharge line only shows up when the selected saved PM is a
  // Card (not ACH) and surcharge is enabled in business_config.
  const [pricePreview, setPricePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Desk-display session — minted lazily when the admin clicks the
  // "Show on customer display" button so we don't burn DB rows on
  // every modal open.
  const [deskSessionId, setDeskSessionId] = useState(null);
  const [deskSessionUrl, setDeskSessionUrl] = useState(null);
  const [deskCreating, setDeskCreating] = useState(false);
  const debounceRef = useRef(null);
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setResults([]);
      setShowResults(false);
      setPickedId(null);
      setSavedPMs(null);
      // Best-effort close the desk session when the modal closes so
      // the customer page flips to "Session ended".
      if (deskSessionId) {
        api.delete(`/admin/desk-display/sessions/${deskSessionId}`).catch(() => {});
        setDeskSessionId(null);
        setDeskSessionUrl(null);
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch saved Stripe PMs whenever an existing customer is picked.
  useEffect(() => {
    if (!pickedId) {
      setSavedPMs(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingPMs(true);
      try {
        const { data } = await api.get(`/admin/parent/${pickedId}/payment-methods`);
        if (cancelled) return;
        setSavedPMs(Array.isArray(data?.payment_methods) ? data.payment_methods : []);
      } catch {
        if (!cancelled) setSavedPMs([]);
      } finally {
        if (!cancelled) setLoadingPMs(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pickedId]);

  // Live price preview — fetches whenever lane / duration / equipment
  // / discounts / saved-PM choice change. We derive `apply_cc_surcharge`
  // from the selected PM: cards trigger surcharge, ACH never does.
  const selectedPM = useMemo(() => {
    if (!form.charge_saved_pm || !savedPMs) return null;
    return savedPMs.find(p => p.id === form.saved_payment_method_id) || null;
  }, [form.charge_saved_pm, form.saved_payment_method_id, savedPMs]);

  const applyCcSurcharge = !!selectedPM && selectedPM.type === 'card';
  const discountFromForm = useMemo(() => {
    const flat = Number(form.discount_amount || 0);
    return Math.max(0, flat);
  }, [form.discount_amount]);
  const discountPctFromForm = useMemo(() => {
    const pct = Number(form.discount_percent || 0);
    return Math.max(0, Math.min(100, pct));
  }, [form.discount_percent]);
  const priceOverrideFromForm = useMemo(() => {
    const v = form.price_override;
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }, [form.price_override]);

  // Push the current snapshot to the desk-display session (debounced 400ms)
  // every time the modal state changes. No-op when no session is active.
  useEffect(() => {
    if (!open || !deskSessionId) return;
    const handle = setTimeout(() => {
      const laneObj = lanes?.find((l) => l.id === form.lane_id);
      // Derive a friendly payment method label so the customer can read
      // "Visa •••• 4242" / "Cash" / etc. on the display.
      let paymentLabel = null;
      if (form.charge_saved_pm && selectedPM) {
        paymentLabel = selectedPM.label;
      } else if (form.mark_paid && form.paid_payment_method) {
        paymentLabel = String(form.paid_payment_method).replace(/_/g, ' ');
      }
      const equipmentLines = (form.equipment_rentals || []).map((l) => ({
        name: l.name || l.equipment_name || 'Equipment',
        qty: l.quantity || l.qty || 1,
        line_total: Number(l.line_total || l.total || 0),
      }));
      api.patch(`/admin/desk-display/sessions/${deskSessionId}`, {
        customer_name: form.customer_name || null,
        lane_name: laneObj?.name || null,
        date: form.date || null,
        start_time: form.start_time || null,
        duration_minutes: form.duration ? Number(form.duration) : null,
        notes: form.notes || null,
        equipment_lines: equipmentLines,
        price: pricePreview || null,
        payment_method_label: paymentLabel,
        status: 'collecting',
      }).catch(() => {});
    }, 400);
    return () => clearTimeout(handle);
  }, [
    open, deskSessionId,
    form.customer_name, form.lane_id, form.date, form.start_time, form.duration,
    form.notes, form.equipment_rentals, form.charge_saved_pm, form.mark_paid,
    form.paid_payment_method, pricePreview, selectedPM, lanes,
  ]);

  const startDeskSession = async () => {
    if (deskSessionId || deskCreating) return;
    setDeskCreating(true);
    try {
      const { data } = await api.post('/admin/desk-display/sessions', { label: 'Walk-in modal' });
      setDeskSessionId(data.session_id);
      setDeskSessionUrl(`${window.location.origin}${data.url_path}`);
    } catch (e) {
      // surfaces only inline; non-blocking
    } finally {
      setDeskCreating(false);
    }
  };

  const copyDeskUrl = async () => {
    if (!deskSessionUrl) return;
    try {
      await navigator.clipboard.writeText(deskSessionUrl);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!open) return;
    if (!form.lane_id || !form.duration) {
      setPricePreview(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const params = new URLSearchParams({
          lane_id: String(form.lane_id),
          duration: String(form.duration),
          equipment_total: String(form.equipment_total || 0),
          discount: String(discountFromForm),
          discount_percent: String(discountPctFromForm),
          apply_cc_surcharge: applyCcSurcharge ? 'true' : 'false',
        });
        if (priceOverrideFromForm !== null) {
          params.set('price_override', String(priceOverrideFromForm));
        }
        const { data } = await api.get(`/lanes/admin/bookings/price-preview?${params.toString()}`);
        if (!cancelled) setPricePreview(data);
      } catch {
        if (!cancelled) setPricePreview(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, form.lane_id, form.duration, form.equipment_total, discountFromForm, discountPctFromForm, priceOverrideFromForm, applyCcSurcharge]);

  const runSearch = async (q) => {
    if (!q || q.length < 1) {
      setResults([]);
      setShowResults(false);
      return;
    }
    try {
      setLoading(true);
      const { data } = await api.get(`/lanes/admin/customer-search?q=${encodeURIComponent(q)}`);
      setResults(data.results || []);
      setShowResults(true);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const onNameChange = (value) => {
    setForm({ ...form, customer_name: value });
    if (pickedId) setPickedId(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(value), 200);
  };

  const pickUser = (u) => {
    setForm({
      ...form,
      customer_name: u.name || '',
      customer_email: u.email || '',
      customer_phone: u.phone || '',
      customer_id: u.id,
    });
    setPickedId(u.id);
    setShowResults(false);
  };

  const derivedEndTime = useMemo(() => {
    if (!form.start_time || !form.duration) return '';
    const [h, m] = String(form.start_time).split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return '';
    const total = h * 60 + m + parseInt(form.duration, 10);
    const eh = Math.floor(total / 60) % 24;
    const em = total % 60;
    return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
  }, [form.start_time, form.duration]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="admin-booking-modal">
        <DialogHeader>
          <DialogTitle>Create Walk-in Booking</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2 relative">
            <Label>Customer Name *</Label>
            <Input
              ref={nameInputRef}
              value={form.customer_name || ''}
              onChange={(e) => onNameChange(e.target.value)}
              onFocus={() => { if (results.length > 0) setShowResults(true); }}
              onBlur={() => setTimeout(() => setShowResults(false), 150)}
              placeholder="Start typing to search existing customers…"
              data-testid="admin-booking-name-input"
              autoComplete="off"
            />
            {pickedId && (
              <p className="text-[11px] text-emerald-600 flex items-center gap-1" data-testid="admin-booking-picked-indicator">
                ✓ Existing user selected — email &amp; phone auto-filled
              </p>
            )}
            {/* Past-due banner for the picked customer. Auto-dismisses
                once all dues are cleared. Soft warning only — admin can
                still proceed with the booking even if dues remain. */}
            {pickedId && (
              <PastDuePanel customerId={pickedId} />
            )}
            {showResults && (results.length > 0 || loading) && (
              <div
                className="absolute z-50 left-0 right-0 top-full mt-1 max-h-60 overflow-y-auto rounded-md border bg-white shadow-lg"
                data-testid="admin-booking-search-results"
              >
                {loading && (
                  <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>
                )}
                {results.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onMouseDown={() => pickUser(u)}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0 transition"
                    data-testid={`admin-booking-search-result-${u.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-slate-900 truncate">{u.name || '(no name)'}</span>
                      <Badge className={`${ROLE_COLOR[u.role] || ROLE_COLOR.customer} text-[10px] capitalize`}>
                        {u.role || 'customer'}
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-500 truncate">{u.email}{u.phone ? ` · ${u.phone}` : ''}</div>
                  </button>
                ))}
                {!loading && results.length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-500">
                    No match — will create a new customer on save.
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Email *</Label>
            <Input
              type="email"
              value={form.customer_email}
              onChange={(e) => { setForm({ ...form, customer_email: e.target.value }); if (pickedId) setPickedId(null); }}
              data-testid="admin-booking-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Phone *</Label>
            <Input
              value={form.customer_phone}
              onChange={(e) => { setForm({ ...form, customer_phone: e.target.value }); if (pickedId) setPickedId(null); }}
              data-testid="admin-booking-phone-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Lane</Label>
              <Select value={form.lane_id} onValueChange={(v) => setForm({ ...form, lane_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {lanes.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Duration</Label>
              <Select value={form.duration} onValueChange={(v) => setForm({ ...form, duration: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 min</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="90">1.5 hours</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Start Time</Label>
              <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </div>
          <EquipmentPicker
            date={form.date}
            startTime={form.start_time}
            endTime={derivedEndTime}
            value={form.equipment_rentals || []}
            onChange={(lines) => setForm({ ...form, equipment_rentals: lines })}
            onTotalChange={(t) => setForm((prev) => prev.equipment_total === t ? prev : { ...prev, equipment_total: t })}
          />

          {/* Admin price override — collapsed by default to keep the
              walk-in flow fast. Open when the admin wants to give a
              comp price, % discount, or set a specific dollar amount.
              The backend preserves the original (config-derived) price
              on the booking row + writes an audit log entry. */}
          <details className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="admin-booking-price-override-block">
            <summary className="text-sm font-medium text-slate-700 cursor-pointer select-none">
              Adjust price (optional)
            </summary>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
              <div>
                <Label className="text-xs">Override $</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g. 25.00"
                  value={form.price_override ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, price_override: e.target.value === '' ? null : Number(e.target.value) })
                  }
                  className="h-9"
                  data-testid="admin-booking-price-override"
                />
              </div>
              <div>
                <Label className="text-xs">Discount %</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  placeholder="e.g. 20"
                  value={form.discount_percent ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, discount_percent: e.target.value === '' ? null : Number(e.target.value) })
                  }
                  className="h-9"
                  data-testid="admin-booking-discount-percent"
                />
              </div>
              <div>
                <Label className="text-xs">Discount $</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g. 5"
                  value={form.discount_amount ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, discount_amount: e.target.value === '' ? null : Number(e.target.value) })
                  }
                  className="h-9"
                  data-testid="admin-booking-discount-amount"
                />
              </div>
              <div className="sm:col-span-3">
                <Label className="text-xs">Reason (audit log)</Label>
                <Input
                  value={form.price_adjustment_reason || ''}
                  onChange={(e) => setForm({ ...form, price_adjustment_reason: e.target.value })}
                  placeholder="e.g. Off-peak comp, returning member discount, etc."
                  className="h-9"
                  data-testid="admin-booking-price-reason"
                />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Override wins over discounts. Original price is preserved for accountability.
            </p>
          </details>

          {/* Live price breakdown — always visible whenever lane + duration
              are set, so the admin (and the customer-facing terminal) can
              see exactly what's being billed BEFORE Confirm. CC surcharge
              only appears when a saved Card is selected; ACH or offline
              methods skip the line entirely. */}
          {form.lane_id && form.duration && (
            <div
              className="rounded-md border border-slate-200 bg-white p-3 text-sm"
              data-testid="admin-booking-price-breakdown"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Price summary</span>
                <div className="flex items-center gap-2">
                  {!deskSessionId ? (
                    <button
                      type="button"
                      onClick={startDeskSession}
                      disabled={deskCreating}
                      className="text-[11px] underline text-blue-700 hover:text-blue-900 disabled:opacity-50"
                      data-testid="admin-booking-open-desk-display"
                    >
                      {deskCreating ? 'Creating…' : 'Show on customer display'}
                    </button>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <a
                        href={deskSessionUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] underline text-emerald-700"
                        data-testid="admin-booking-desk-display-link"
                      >
                        Open display ↗
                      </a>
                      <button
                        type="button"
                        onClick={copyDeskUrl}
                        className="text-[11px] text-slate-500 hover:text-slate-800"
                        data-testid="admin-booking-desk-display-copy"
                      >
                        Copy link
                      </button>
                    </div>
                  )}
                  {previewLoading && (
                    <span className="text-[10px] text-slate-400 animate-pulse">recalculating…</span>
                  )}
                </div>
              </div>
              {pricePreview ? (
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-slate-700">
                    <span>Lane ({form.duration} min)</span>
                    <span className="font-mono">{cs()}{pricePreview.lane_price.toFixed(2)}</span>
                  </div>
                  {pricePreview.equipment_total > 0 && (
                    <div className="flex justify-between text-slate-700">
                      <span>Equipment</span>
                      <span className="font-mono">{cs()}{pricePreview.equipment_total.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-slate-700 border-t border-slate-100 pt-1">
                    <span>Subtotal</span>
                    <span className="font-mono">{cs()}{pricePreview.subtotal.toFixed(2)}</span>
                  </div>
                  {pricePreview.discount > 0 && (
                    <div className="flex justify-between text-emerald-700">
                      <span>Discount</span>
                      <span className="font-mono">- {cs()}{pricePreview.discount.toFixed(2)}</span>
                    </div>
                  )}
                  {pricePreview.cc_surcharge_amount > 0 && (
                    <div
                      className="flex justify-between text-amber-700"
                      data-testid="admin-booking-cc-surcharge-line"
                    >
                      <span>
                        CC processing fee ({pricePreview.cc_surcharge_percent}%)
                      </span>
                      <span className="font-mono">+ {cs()}{pricePreview.cc_surcharge_amount.toFixed(2)}</span>
                    </div>
                  )}
                  <div
                    className="flex justify-between text-slate-900 font-semibold border-t border-slate-300 pt-1.5 mt-1.5"
                    data-testid="admin-booking-total"
                  >
                    <span>Total billed to customer</span>
                    <span className="font-mono">{cs()}{pricePreview.total.toFixed(2)}</span>
                  </div>
                  {applyCcSurcharge && pricePreview.cc_surcharge_amount === 0 && pricePreview.cc_surcharge_enabled === false && (
                    <p className="text-[10px] text-slate-400 mt-1">
                      CC surcharge is disabled in payment settings — no fee added.
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-400">Pick a lane and duration to see the price.</p>
              )}
            </div>
          )}

          {/* Bill & Mark Paid — for walk-ins who pay at the desk
              (cash / check / Zelle / Venmo / card). When checked, the
              booking is saved AND a payment_transactions row is recorded
              in one round-trip; the booking lands as `paid` instead of
              `pending`. Soft-fails the payment step if anything throws
              so the booking itself is never lost. */}
          <details
            className={`rounded-md border ${form.mark_paid ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200 bg-slate-50/40'}`}
            open={!!form.mark_paid}
            data-testid="admin-booking-mark-paid-block"
          >
            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-slate-700 flex items-center justify-between">
              <span>Bill &amp; Mark Paid (collect payment at the desk)</span>
              <Checkbox
                checked={!!form.mark_paid}
                onCheckedChange={(checked) => setForm({
                  ...form,
                  mark_paid: !!checked,
                  // Reset saved-PM selection when the block closes so
                  // we don't accidentally double-bill.
                  ...(checked ? {} : { charge_saved_pm: false, saved_payment_method_id: null }),
                })}
                onClick={(e) => e.stopPropagation()}
                data-testid="admin-booking-mark-paid-toggle"
              />
            </summary>
            {form.mark_paid && (
              <div className="px-3 pb-3 pt-1 space-y-3">
                {/* iter307bc — NEW first option: open a Stripe Checkout
                    that ALSO saves the card on file, so future bookings
                    can use the "Charge Saved PM" flow below. Mutually
                    exclusive with the other paths. */}
                {pickedId && (
                  <label
                    className={`flex items-start gap-2 p-3 rounded-md border cursor-pointer ${
                      form.charge_via_stripe_new
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                    data-testid="admin-booking-stripe-new-card-block"
                  >
                    <input
                      type="radio"
                      name="admin-booking-pay-method"
                      checked={!!form.charge_via_stripe_new}
                      onChange={() => setForm({
                        ...form,
                        charge_via_stripe_new: true,
                        // mutually exclusive
                        charge_saved_pm: false,
                        saved_payment_method_id: null,
                      })}
                      data-testid="admin-booking-stripe-new-card-radio"
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-800">
                        Charge via Stripe — save card on file
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Opens Stripe Checkout in a new tab. After payment, the
                        card is saved to the customer for one-click future
                        "Charge Saved PM" bookings. <strong>Booking will be
                        marked paid by the Stripe webhook</strong> on success.
                      </div>
                    </div>
                  </label>
                )}
                {/* Saved Stripe Payment Methods for the picked customer.
                    When the admin picks one, we switch into "Charge Saved
                    PM" mode and hide the offline fields (mutually exclusive). */}
                {pickedId && (
                  <div
                    className="rounded-md border border-blue-200 bg-blue-50/50 p-2"
                    data-testid="admin-booking-saved-pm-block"
                  >
                    <div className="text-xs font-medium text-slate-700 mb-2">
                      Charge a saved card / bank on file
                    </div>
                    {loadingPMs && (
                      <div className="text-xs text-slate-500" data-testid="admin-booking-saved-pm-loading">
                        Loading saved payment methods…
                      </div>
                    )}
                    {!loadingPMs && savedPMs && savedPMs.length === 0 && (
                      <div className="text-xs text-slate-500" data-testid="admin-booking-saved-pm-empty">
                        No saved card or bank on file. Use "Charge via Stripe — save card on file" above to capture one for next time.
                      </div>
                    )}
                    {!loadingPMs && savedPMs && savedPMs.length > 0 && (
                      <div className="space-y-1.5">
                        {savedPMs.map((pm) => (
                          <label
                            key={pm.id}
                            className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-sm ${
                              form.charge_saved_pm && form.saved_payment_method_id === pm.id
                                ? 'border-blue-500 bg-blue-100/60'
                                : 'border-slate-200 bg-white hover:bg-slate-50'
                            }`}
                            data-testid={`admin-booking-saved-pm-${pm.id}`}
                          >
                            <input
                              type="radio"
                              name="admin-booking-pay-method"
                              checked={form.charge_saved_pm && form.saved_payment_method_id === pm.id}
                              onChange={() => setForm({
                                ...form,
                                charge_saved_pm: true,
                                saved_payment_method_id: pm.id,
                                charge_via_stripe_new: false,
                              })}
                              data-testid={`admin-booking-saved-pm-radio-${pm.id}`}
                            />
                            <span className="flex-1">{pm.label}</span>
                            <Badge className={pm.type === 'card' ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'}>
                              {pm.type === 'card' ? 'Card' : 'ACH'}
                            </Badge>
                          </label>
                        ))}
                        {form.charge_saved_pm && (
                          <button
                            type="button"
                            className="text-[11px] text-slate-500 underline hover:text-slate-700"
                            onClick={() => setForm({
                              ...form,
                              charge_saved_pm: false,
                              saved_payment_method_id: null,
                            })}
                            data-testid="admin-booking-saved-pm-clear"
                          >
                            Clear selection (use offline method instead)
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {/* Offline payment fields — hidden when a saved PM or
                    new-card Stripe Checkout is selected (mutually exclusive). */}
                {!form.charge_saved_pm && !form.charge_via_stripe_new && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">
                        Payment method <span className="text-rose-600">*</span>
                      </Label>
                      <Select
                        value={form.paid_payment_method || 'cash'}
                        onValueChange={(v) => setForm({ ...form, paid_payment_method: v })}
                      >
                        <SelectTrigger className="h-9" data-testid="admin-booking-paid-method">
                          <SelectValue placeholder="Cash" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="check">Check</SelectItem>
                          <SelectItem value="ach">ACH / Bank Transfer</SelectItem>
                          <SelectItem value="zelle">Zelle</SelectItem>
                          <SelectItem value="venmo">Venmo</SelectItem>
                          <SelectItem value="card">Card (offline / terminal)</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Reference # (optional)</Label>
                      <Input
                        value={form.paid_reference_number || ''}
                        onChange={(e) => setForm({ ...form, paid_reference_number: e.target.value })}
                        placeholder="Check #, Zelle confirmation, etc."
                        className="h-9"
                        data-testid="admin-booking-paid-reference"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Notes (optional)</Label>
                      <Input
                        value={form.paid_notes || ''}
                        onChange={(e) => setForm({ ...form, paid_notes: e.target.value })}
                        placeholder="Anything you want on the audit row"
                        className="h-9"
                        data-testid="admin-booking-paid-notes"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </details>

          {/* Terms & Conditions consent — required by policy. The customer
              physically agrees in person and the admin confirms by ticking
              this box; the consent timestamp is stamped on the customer
              record by the backend. */}
          <div
            className={`flex items-start gap-2 p-3 rounded-md border ${
              form.terms_accepted ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'
            }`}
            data-testid="admin-booking-terms-block"
          >
            <Checkbox
              id="admin-booking-terms"
              checked={!!form.terms_accepted}
              onCheckedChange={(checked) => setForm({ ...form, terms_accepted: !!checked })}
              data-testid="admin-booking-terms-checkbox"
              className="mt-0.5"
            />
            <Label htmlFor="admin-booking-terms" className="text-sm cursor-pointer leading-snug">
              Customer agrees to the{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
                data-testid="admin-booking-terms-link"
                onClick={(e) => e.stopPropagation()}
              >
                Terms &amp; Conditions
              </a>
              <span className="text-rose-600"> *</span>
              <span className="block text-xs text-slate-500 mt-0.5">
                Required. By ticking, you confirm the walk-in customer has reviewed and accepted the policy.
              </span>
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={onSubmit}
            disabled={!form.terms_accepted}
            className="bg-green-500 hover:bg-green-600 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="admin-booking-submit"
          >
            Create Booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminBookingModal;
